import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import type { QuoteFlowStatus } from './types'

/**
 * Teklif süreci: her kayıt yeni revizyon (açıklama zorunlu); iskonto personel sınırını aşarsa yönetici, her durumda son olarak
 * firma sahibi onaylar; onaylı revizyon müşteriye gönderenin posta kutusundan, tüm firma sahipleri CC'de gönderilir.
 */
export const QUOTE_PERMS = {
  write: 'sales:quote:write',
  approveManager: 'sales:quote:approve_manager',
  approveOwner: 'sales:quote:approve_owner',
  send: 'sales:quote:send',
  visibilityLog: 'sales:quote:visibility_log',
  template: 'sales:quote:template_manage',
} as const

export const FLOW_TONE: Record<QuoteFlowStatus, 'neutral' | 'warning' | 'info' | 'success' | 'danger'> = {
  taslak: 'neutral',
  manager_review: 'warning',
  owner_review: 'warning',
  approved: 'info',
  sent: 'info',
  negotiation: 'warning',
  final: 'info',
  accepted: 'success',
  rejected: 'danger',
}

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))

export type SnapshotLine = { line_type: string; ref_code: string; description: string; unit_price: string; discount: string; qty: number; line_total?: string }
export type QuoteSnapshot = {
  discount_percent: string
  subtotal: string
  discount_amount: string
  total: string
  notes: string
  quote_date: string
  valid_until: string
  show_line_prices?: boolean
  effective_discount?: string
  lines: SnapshotLine[]
}

export type QuoteRevision = { revision: number; reason: string; createdBy: string; createdAt: string; snapshot: QuoteSnapshot }
export type QuoteComment = { userId: string; kind: 'comment' | 'negotiation' | 'decision' | 'system'; body: string; revision: number; createdAt: string }
export type QuoteSend = { sentBy: string; revision: number; recipients: string[]; cc: string[]; subject: string; format: string; createdAt: string }
export type VisibilityChange = { userId: string; show: boolean; reason: string; createdAt: string }

export type QuoteFlow = {
  id: string
  number: string
  status: QuoteFlowStatus
  projectId: string
  revision: number
  currentApproved: boolean
  isFinal: boolean
  showLinePrices: boolean
  effectiveDiscount: string
  tier: 'staff' | 'manager' | 'owner'
  approvedBy: string
  approvedAt: string
  managerApprovedBy: string
  submittedBy: string
  submittedAt: string
  sentAt: string
  revisions: QuoteRevision[]
  comments: QuoteComment[]
  sends: QuoteSend[]
  visibility: VisibilityChange[] | null
  canEdit: boolean
  canSubmit: boolean
  canDecide: boolean
  canSend: boolean
  canNegotiate: boolean
  canTogglePrices: boolean
}

const mapFlow = (r: Row): QuoteFlow => ({
  id: s(r.id),
  number: s(r.number),
  status: s(r.status) as QuoteFlowStatus,
  projectId: s(r.project_id),
  revision: Number(r.revision) || 1,
  currentApproved: r.current_approved === true,
  isFinal: r.is_final === true,
  showLinePrices: r.show_line_prices === true,
  effectiveDiscount: s(r.effective_discount),
  tier: (s(r.discount_tier) || 'staff') as QuoteFlow['tier'],
  approvedBy: s(r.approved_by),
  approvedAt: s(r.approved_at),
  managerApprovedBy: s(r.manager_approved_by),
  submittedBy: s(r.submitted_by),
  submittedAt: s(r.submitted_at),
  sentAt: s(r.sent_at),
  revisions: asList(r.revisions as Row[]).map((x) => ({
    revision: Number(x.revision),
    reason: s(x.reason),
    createdBy: s(x.created_by),
    createdAt: s(x.created_at),
    snapshot: (x.snapshot ?? { lines: [] }) as QuoteSnapshot,
  })),
  comments: asList(r.comments as Row[]).map((c) => ({
    userId: s(c.user_id),
    kind: s(c.kind) as QuoteComment['kind'],
    body: s(c.body),
    revision: Number(c.revision),
    createdAt: s(c.created_at),
  })),
  sends: asList(r.sends as Row[]).map((x) => ({
    sentBy: s(x.sent_by),
    revision: Number(x.revision),
    recipients: Array.isArray(x.recipients) ? x.recipients.map(s) : [],
    cc: Array.isArray(x.cc) ? x.cc.map(s) : [],
    subject: s(x.subject),
    format: s(x.format),
    createdAt: s(x.created_at),
  })),
  visibility: Array.isArray(r.visibility_log)
    ? (r.visibility_log as Row[]).map((v) => ({ userId: s(v.user_id), show: v.show === true, reason: s(v.reason), createdAt: s(v.created_at) }))
    : null,
  canEdit: r.can_edit === true,
  canSubmit: r.can_submit === true,
  canDecide: r.can_decide === true,
  canSend: r.can_send === true,
  canNegotiate: r.can_negotiate === true,
  canTogglePrices: r.can_toggle_prices === true,
})

const flowKey = (id: string) => ['crm', 'quote-flow', id] as const

export const useQuoteFlow = (id: string | null) =>
  useQuery({ queryKey: flowKey(id ?? ''), enabled: !!id, queryFn: async () => mapFlow(await api.get<Row>(`/api/v1/quotes/${id}/flow`)) })

function useQuoteMut<A>(fn: (a: A) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['crm', 'quote-flow'] }), qc.invalidateQueries({ queryKey: ['crm', 'offers'] })]),
  })
}

export const useQuoteAction = () =>
  useQuoteMut(
    ({ id, action, body }: { id: string; action: 'submit' | 'decide' | 'negotiate' | 'comments' | 'line-prices'; body: Record<string, unknown> }) =>
      api.post(`/api/v1/quotes/${id}/${action}`, body),
  )

export type QuoteLineInput = { lineType: 'module' | 'extra'; refCode: string; description: string; qty: number; unitPrice: string; discount: string }
export type QuoteInput = {
  customerId: string
  projectId: string
  date: string
  validUntil: string
  discountPct: string
  note: string
  lines: QuoteLineInput[]
  reason: string
}

const quoteBody = (i: QuoteInput) => ({
  account_id: i.customerId,
  project_id: i.projectId,
  quote_date: i.date,
  valid_until: i.validUntil,
  discount_percent: Number(i.discountPct.replace(',', '.') || 0).toFixed(2),
  notes: i.note,
  reason: i.reason,
  lines: i.lines.map((l) => ({
    line_type: l.lineType,
    ref_code: l.lineType === 'module' ? l.refCode : '',
    description: l.lineType === 'extra' ? l.description : '',
    qty: l.qty,
    unit_price: l.unitPrice.replace(',', '.'),
    discount: (l.discount || '0').replace(',', '.'),
  })),
})

export const useSaveQuote = () =>
  useQuoteMut(async ({ id, input }: { id?: string; input: QuoteInput }) => {
    if (id) return api.put(`/api/v1/quotes/${id}`, quoteBody(input))
    return api.post('/api/v1/quotes', quoteBody(input))
  })

export type SendDraft = { to: string[]; ownersCc: string[]; subject: string; message: string }

export const useSendDraft = (id: string | null) =>
  useQuery({
    queryKey: ['crm', 'quote-send-draft', id],
    enabled: !!id,
    queryFn: async (): Promise<SendDraft> => {
      const r = await api.get<Row>(`/api/v1/quotes/${id}/send-draft`)
      return {
        to: Array.isArray(r.to) ? r.to.map(s) : [],
        ownersCc: Array.isArray(r.owners_cc) ? r.owners_cc.map(s) : [],
        subject: s(r.subject),
        message: s(r.message),
      }
    },
  })

export const useSendQuote = () =>
  useQuoteMut(({ id, to, cc, subject, message, format }: { id: string; to: string[]; cc: string[]; subject: string; message: string; format: 'pdf' | 'docx' }) =>
    api.post(`/api/v1/quotes/${id}/send`, { to, cc, subject, message, format }),
  )

export const downloadQuote = (id: string, format: 'pdf' | 'docx') => api.download(`/api/v1/quotes/${id}/document`, `Urunver_Teklif.${format}`, { format })

export type QuoteTemplate = {
  title: string
  intro: string
  terms: string
  footer: string
  mailSubject: string
  mailBody: string
  showSignatures: boolean
  managerMaxDiscount: string
}

export const useQuoteTemplate = () =>
  useQuery({
    queryKey: ['crm', 'quote-template'],
    queryFn: async (): Promise<QuoteTemplate> => {
      const r = await api.get<Row>('/api/v1/sales/quote-template')
      return {
        title: s(r.title),
        intro: s(r.intro),
        terms: s(r.terms),
        footer: s(r.footer),
        mailSubject: s(r.mail_subject),
        mailBody: s(r.mail_body),
        showSignatures: r.show_signatures === true,
        managerMaxDiscount: s(r.manager_max_discount),
      }
    },
  })

export const useSaveQuoteTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (t: QuoteTemplate) =>
      api.put('/api/v1/sales/quote-template', {
        title: t.title,
        intro: t.intro,
        terms: t.terms,
        footer: t.footer,
        mail_subject: t.mailSubject,
        mail_body: t.mailBody,
        show_signatures: t.showSignatures,
        manager_max_discount: t.managerMaxDiscount.replace(',', '.') || '0',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'quote-template'] }),
  })
}
